/**
 * WORKER STORE
 *
 * Zustand store for managing the local Python worker process (Tauri desktop only).
 * All Tauri API calls use dynamic imports to avoid breaking web builds.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { isTauri } from "@/hooks/usePlatform";
import type {
  DepCheck,
  WorkerState,
  DepsStatus,
  LocalJob,
  WorkerLog,
} from "@/types/worker";

// ── Dynamic Tauri invoke helper ─────────────────────────────────────

async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

function assertTauri(action: string): void {
  if (!isTauri()) {
    throw new Error(`${action} is only available in the desktop app`);
  }
}

// ── Constants ───────────────────────────────────────────────────────

const MAX_LOGS = 500;
const WORKER_STATUS_POLL_INTERVAL_MS = 250;
const WORKER_STATUS_POLL_TIMEOUT_MS = 12_000;

// Tauri IPC command names
const CMD_START = "worker_start";
const CMD_STOP = "worker_stop";
const CMD_RESTART = "worker_restart";
const CMD_CHECK_DEPS = "worker_check_deps";
const CMD_SUBMIT_JOB = "worker_submit_job";
const CMD_RESUME_JOB = "worker_resume_job";
const CMD_LIST_JOBS = "worker_list_jobs";

type RawDepsStatus = {
  python?: string | DepCheck | null;
  git?: string | DepCheck | null;
  claude?: string | DepCheck | null;
  api_key?: boolean | DepCheck | null;
  all_ok?: boolean;
};

type WorkerSubmitPayload = {
  taskId: string;
  eventType: string;
  workflowId?: string;
  graph?: Record<string, unknown> | null;
  workDir?: string;
  envVars?: Record<string, string>;
};

type WorkerResumePayload = {
  taskId: string;
  runId: string;
  resumeFromNode: string;
  workflowId?: string;
  graph?: Record<string, unknown> | null;
  workDir?: string;
  envVars?: Record<string, string>;
};

type WorkerStatusPayload = {
  state?: WorkerState | string | null;
  pid?: number | null;
  message?: string | null;
};

function toErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  try {
    const serialized = JSON.stringify(error);
    if (serialized && serialized !== "{}") {
      return serialized;
    }
  } catch {
    // Ignore serialization issues and fall back to a generic message.
  }

  return "Unknown worker error";
}

function normalizeDep(raw: unknown, fallbackError: string): DepCheck {
  if (raw && typeof raw === "object" && "ok" in (raw as Record<string, unknown>)) {
    const dep = raw as DepCheck;
    return {
      ok: Boolean(dep.ok),
      version: dep.version,
      error: dep.error,
    };
  }

  if (typeof raw === "string" && raw.trim().length > 0) {
    return {
      ok: true,
      version: raw.trim(),
    };
  }

  return {
    ok: false,
    error: fallbackError,
  };
}

function normalizeApiKeyDep(raw: unknown): DepCheck {
  if (raw && typeof raw === "object" && "ok" in (raw as Record<string, unknown>)) {
    const dep = raw as DepCheck;
    return {
      ok: Boolean(dep.ok),
      version: dep.version,
      error: dep.error,
    };
  }

  if (typeof raw === "boolean") {
    return raw ? { ok: true } : { ok: false, error: "Missing API key" };
  }

  return {
    ok: false,
    error: "Missing API key",
  };
}

function normalizeDepsStatus(raw: unknown): DepsStatus {
  const input = (raw ?? {}) as RawDepsStatus;
  const python = normalizeDep(
    input.python,
    "Python not found. Install Python 3.10+ (macOS: brew install python), restart Colony, then check again."
  );
  const git = normalizeDep(
    input.git,
    "Git not found. Install Git (macOS: xcode-select --install or brew install git), restart Colony, then check again."
  );
  const claude = normalizeDep(
    input.claude,
    "Claude CLI not found. Install with: npm install -g @anthropic-ai/claude-code, then restart Colony."
  );
  const api_key = normalizeApiKeyDep(input.api_key);
  // Hard requirements for worker start are Python/Git/Claude CLI.
  // API key is optional when the Claude CLI is already authenticated via OAuth.
  const all_ok = python.ok && git.ok && claude.ok;

  return {
    python,
    git,
    claude,
    api_key,
    all_ok,
  };
}

function normalizeJob(raw: unknown): LocalJob | null {
  if (!raw || typeof raw !== "object") return null;
  const job = raw as Record<string, unknown>;
  const id = String(job.id ?? "");
  const taskId = String(job.taskId ?? job.task_id ?? "");
  const eventType = String(job.eventType ?? job.event_type ?? "execute_workflow");
  const status = String(job.status ?? "queued");
  if (!id || !taskId) return null;
  const createdAt = String(job.createdAt ?? job.created_at ?? new Date().toISOString());
  const updatedAt = String(job.updatedAt ?? job.updated_at ?? createdAt);

  return {
    id,
    taskId,
    eventType,
    status,
    error: typeof job.error === "string" ? job.error : undefined,
    leaseExpiresAt:
      typeof job.leaseExpiresAt === "string"
        ? job.leaseExpiresAt
        : typeof job.lease_expires_at === "string"
        ? job.lease_expires_at
        : undefined,
    createdAt,
    updatedAt,
  };
}

function normalizeWorkerState(raw: unknown): WorkerState {
  const state = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (state === "running" || state === "starting" || state === "error" || state === "stopped") {
    return state as WorkerState;
  }
  return "stopped";
}

function resolveWorkerStatusMessage(status: WorkerStatusPayload, fallback: string): string {
  if (typeof status.message === "string" && status.message.trim().length > 0) {
    return status.message;
  }
  return fallback;
}

async function waitForWorkerStatus(): Promise<WorkerStatusPayload> {
  const deadline = Date.now() + WORKER_STATUS_POLL_TIMEOUT_MS;
  let latest: WorkerStatusPayload = {};

  while (Date.now() < deadline) {
    latest = await tauriInvoke<WorkerStatusPayload>("worker_status");
    const state = normalizeWorkerState(latest?.state);
    if (state === "running" || state === "error" || state === "stopped") {
      return latest;
    }
    await new Promise((resolve) => setTimeout(resolve, WORKER_STATUS_POLL_INTERVAL_MS));
  }

  return latest;
}

async function resolveRunningWorkerStatus(initial: WorkerStatusPayload): Promise<WorkerStatusPayload> {
  const initialState = normalizeWorkerState(initial?.state);
  if (initialState === "running") {
    return initial;
  }
  if (initialState === "starting") {
    const awaited = await waitForWorkerStatus();
    const awaitedState = normalizeWorkerState(awaited?.state);
    if (awaitedState === "running") {
      return awaited;
    }
    throw new Error(
      resolveWorkerStatusMessage(
        awaited,
        awaitedState === "starting"
          ? "Local worker is still starting. Try again in a few seconds."
          : `Local worker is ${awaitedState}.`
      )
    );
  }
  throw new Error(resolveWorkerStatusMessage(initial, `Local worker is ${initialState}.`));
}

function isRecoverableSubmitError(message: string): boolean {
  const normalized = message.toLowerCase();
  return (
    normalized.includes("worker is not running") ||
    normalized.includes("worker process not available") ||
    normalized.includes("stdin channel closed") ||
    normalized.includes("response channel dropped") ||
    normalized.includes("rpc timeout")
  );
}

// ── Store ───────────────────────────────────────────────────────────

interface WorkerStoreState {
  workerState: WorkerState;
  pid: number | null;
  activeJobId: string | null;
  lastError: string | null;
  depsStatus: DepsStatus | null;
  jobs: LocalJob[];
  logs: WorkerLog[];

  // Actions
  start: () => Promise<void>;
  stop: () => Promise<void>;
  restart: () => Promise<void>;
  checkDeps: () => Promise<DepsStatus>;
  submitJob: (job: WorkerSubmitPayload) => Promise<string>;
  resumeJob: (job: WorkerResumePayload) => Promise<string>;
  cancelJob: (jobId: string) => Promise<void>;
  listJobs: (status?: string) => Promise<LocalJob[]>;

  // Internal setters (called by event listeners)
  setWorkerState: (state: WorkerState) => void;
  setPid: (pid: number | null) => void;
  setActiveJobId: (jobId: string | null) => void;
  setDepsStatus: (status: DepsStatus) => void;
  addLog: (log: WorkerLog) => void;
  setJobs: (jobs: LocalJob[]) => void;
  updateJob: (jobId: string, updates: Partial<LocalJob>) => void;

  // Computed
  isWorkerAvailable: () => boolean;

  // Private helpers (not for external use)
  _startOrRestart: (cmd: string, label: string) => Promise<void>;
  _dispatchJob: (label: string, eventType: string, rpcFn: () => Promise<{ job_id?: string; jobId?: string }>, taskId: string) => Promise<string>;
}

export const useWorkerStore = create<WorkerStoreState>()(
  persist((set, get) => ({
  workerState: "stopped",
  pid: null,
  activeJobId: null,
  lastError: null,
  depsStatus: null,
  jobs: [],
  logs: [],

  // ── Actions ─────────────────────────────────────────────────────

  async start() { return this._startOrRestart(CMD_START, "start"); },

  async stop() {
    assertTauri("Worker stop");
    try {
      await tauriInvoke(CMD_STOP);
      set({ workerState: "stopped", pid: null, activeJobId: null, jobs: [], lastError: null });
    } catch (err) {
      const message = toErrorMessage(err);
      set({ workerState: "error", lastError: message });
      get().addLog({
        level: "error",
        message: `Worker stop failed: ${message}`,
        timestamp: new Date().toISOString(),
      });
      throw new Error(message);
    }
  },

  async restart() { return this._startOrRestart(CMD_RESTART, "restart"); },

  async _startOrRestart(cmd: string, label: string) {
    assertTauri(`Worker ${label}`);
    set({ workerState: "starting", logs: [], jobs: [], activeJobId: null, lastError: null });
    try {
      const status = await tauriInvoke<WorkerStatusPayload>(cmd);
      const running = await resolveRunningWorkerStatus(status);
      set({
        workerState: "running",
        pid: typeof running.pid === "number" ? running.pid : null,
        lastError: null,
      });
      try {
        await get().checkDeps();
      } catch {
        // Non-fatal; user can still run manual deps check from Worker panel.
      }
    } catch (err) {
      const message = toErrorMessage(err);
      set({ workerState: "error", lastError: message });
      get().addLog({
        level: "error",
        message: `Worker ${label} failed: ${message}`,
        timestamp: new Date().toISOString(),
      });
      try {
        await get().checkDeps();
      } catch {
        // Ignore deps check failures here; keep original error.
      }
      throw new Error(message);
    }
  },

  async checkDeps(): Promise<DepsStatus> {
    assertTauri("Dependency check");
    const raw = await tauriInvoke<unknown>(CMD_CHECK_DEPS);
    const status = normalizeDepsStatus(raw);
    set({ depsStatus: status });
    return status;
  },

  async submitJob(job): Promise<string> {
    return this._dispatchJob("submit", job.eventType || "execute_workflow", () =>
      tauriInvoke<{ job_id?: string; jobId?: string }>(CMD_SUBMIT_JOB, {
        params: {
          task_id: job.taskId,
          taskId: job.taskId,
          event_type: job.eventType ?? "execute_workflow",
          workflow_id: job.workflowId ?? null,
          workflowId: job.workflowId ?? null,
          graph: job.graph ?? null,
          work_dir: job.workDir ?? "",
          workDir: job.workDir ?? "",
          env_vars: job.envVars ?? {},
          envVars: job.envVars ?? {},
        },
      }),
      job.taskId,
    );
  },

  async resumeJob(job): Promise<string> {
    return this._dispatchJob("resume", "resume_workflow", () =>
      tauriInvoke<{ job_id?: string; jobId?: string }>(CMD_RESUME_JOB, {
        params: {
          task_id: job.taskId,
          run_id: job.runId,
          resume_from_node: job.resumeFromNode,
          workflow_id: job.workflowId ?? "",
          graph: job.graph ?? null,
          work_dir: job.workDir ?? "",
          env_vars: job.envVars ?? {},
        },
      }),
      job.taskId,
    );
  },

  /** Shared dispatch logic for submitJob/resumeJob — handles retry-after-restart and state updates. */
  async _dispatchJob(
    label: string,
    eventType: string,
    rpcFn: () => Promise<{ job_id?: string; jobId?: string }>,
    taskId: string,
  ): Promise<string> {
    assertTauri(`Job ${label}`);

    let response: { job_id?: string; jobId?: string } | null = null;
    try {
      response = await rpcFn();
    } catch (err) {
      const message = toErrorMessage(err);
      if (!isRecoverableSubmitError(message)) {
        get().addLog({ level: "error", message: `Worker ${label} failed: ${message}`, timestamp: new Date().toISOString() });
        throw new Error(message);
      }

      get().addLog({ level: "warn", message: `Worker was not running during ${label}; restarting and retrying once.`, timestamp: new Date().toISOString() });

      try {
        await get().start();
        response = await rpcFn();
      } catch (retryErr) {
        const retryMessage = toErrorMessage(retryErr);
        get().addLog({ level: "error", message: `Worker ${label} failed after restart: ${retryMessage}`, timestamp: new Date().toISOString() });
        throw new Error(retryMessage);
      }
    }

    if (!response) {
      throw new Error(`Worker ${label} failed: empty response`);
    }

    const jobId = response.job_id ?? response.jobId ?? `job-${Date.now()}`;
    const now = new Date().toISOString();
    set((state) => ({
      jobs: [{ id: jobId, taskId, eventType, status: "running" as const, createdAt: now, updatedAt: now }, ...state.jobs.filter((j) => j.id !== jobId)],
      activeJobId: jobId,
    }));

    return jobId;
  },

  async cancelJob(jobId: string) {
    assertTauri("Job cancel");
    const taskId = get().jobs.find((job) => job.id === jobId)?.taskId ?? jobId;
    await tauriInvoke("worker_cancel_job", { task_id: taskId, taskId });
    set((s) => ({
      jobs: s.jobs.map((j) =>
        j.id === jobId
          ? { ...j, status: "cancelled", updatedAt: new Date().toISOString() }
          : j
      ),
      activeJobId: s.activeJobId === jobId ? null : s.activeJobId,
    }));
  },

  async listJobs(status?: string): Promise<LocalJob[]> {
    assertTauri("Job list");
    try {
      const raw = await tauriInvoke<unknown>(CMD_LIST_JOBS);
      const remoteJobs = Array.isArray(raw)
        ? raw.map(normalizeJob).filter((job): job is LocalJob => Boolean(job))
        : [];

      // Merge: remote jobs take precedence for running tasks,
      // but preserve local-only jobs (e.g. just-cancelled via event)
      const remoteById: Record<string, LocalJob> = {};
      remoteJobs.forEach((j) => { remoteById[j.id] = j; });

      const localJobs = get().jobs;
      const merged: Record<string, LocalJob> = {};

      // Start with local jobs
      localJobs.forEach((job) => { merged[job.id] = job; });

      // Overlay remote state (authoritative for running/terminal statuses)
      Object.keys(remoteById).forEach((id) => {
        const remoteJob = remoteById[id];
        const local = merged[id];
        if (!local) {
          merged[id] = remoteJob;
        } else {
          // Remote "running" status wins over local "queued";
          // Remote terminal statuses (completed/failed/cancelled) always win
          const isRemoteTerminal = ["completed", "failed", "cancelled"].includes(remoteJob.status);
          const isLocalTerminal = ["completed", "failed", "cancelled"].includes(local.status);
          if (isRemoteTerminal || (!isLocalTerminal && remoteJob.status === "running")) {
            merged[id] = { ...local, ...remoteJob };
          }
        }
      });

      const allJobs = Object.keys(merged).map((id) => merged[id]);
      const filtered = status ? allJobs.filter((job) => job.status === status) : allJobs;
      set({ jobs: filtered });
      return filtered;
    } catch {
      const jobs = get().jobs;
      return status ? jobs.filter((job) => job.status === status) : jobs;
    }
  },

  // ── Internal setters ────────────────────────────────────────────

  setWorkerState(state: WorkerState) {
    set({ workerState: state });
  },

  setPid(pid: number | null) {
    set({ pid });
  },

  setActiveJobId(jobId: string | null) {
    set({ activeJobId: jobId });
  },

  setDepsStatus(status: DepsStatus) {
    set({ depsStatus: status });
  },

  addLog(log: WorkerLog) {
    set((s) => {
      const logs = [...s.logs, log];
      // Keep circular buffer at MAX_LOGS
      if (logs.length > MAX_LOGS) {
        return { logs: logs.slice(logs.length - MAX_LOGS) };
      }
      return { logs };
    });
  },

  setJobs(jobs: LocalJob[]) {
    set({ jobs });
  },

  updateJob(jobId: string, updates: Partial<LocalJob>) {
    set((s) => ({
      jobs: s.jobs.map((j) => (j.id === jobId ? { ...j, ...updates } : j)),
    }));
  },

  // ── Computed ────────────────────────────────────────────────────

  isWorkerAvailable(): boolean {
    const { workerState, depsStatus } = get();
    const depsReady = depsStatus ? depsStatus.all_ok : false;
    return (
      isTauri() &&
      workerState === "running" &&
      depsReady
    );
  },
  }), {
    name: "factory-worker-store",
    partialize: (state) => ({
      logs: state.logs,
    }),
  }),
);
